import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {
    View,
    TouchableWithoutFeedback,
    Text
} from 'react-native'

import {
    initData,
    drawYAxis,
    drawGuideLine,
    drawYAxisLabels,
    numberWithCommas,
    drawXAxis,
    drawXAxisLabels
} from './chartHelpers'

import styles from './styles'

export default class Charts extends Component {
    static propTypes = {
        data: PropTypes.array.isRequired,
        offsetKeyDate: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ]),
        layout: PropTypes.shape({
            width: PropTypes.number,
            height: PropTypes.number
        }),
        primaryColor: PropTypes.string,
        selectedColor: PropTypes.string,
        noAxisLabels: PropTypes.bool,
        noSelectBox: PropTypes.bool,
        onPointClick: PropTypes.func
    }

    static defaultProps = {
        data: [],
        offsetKeyDate: { unit: 'week' },
        layout: { width: 300, height: 400 },
        primaryColor: '#FF009B',
        selectedColor: '#FF0000',
        noAxisLabels: false,
        noSelectBox: false,
        onPointClick: (point) => {}
    }

    constructor(
        props
    ) {
        super(props)

        this.state = {
            ...{ loading: false },
            ...initData(props.data, props.offsetKeyDate, props.layout, props.noAxisLabels)
        }
    }

    shouldComponentUpdate(
        nextProps,
        nextState
    ) {
        if (
            nextState.sortedData !== this.state.sortedData ||
            nextState.selectedIndex !== this.state.selectedIndex ||
            nextState.scrollPosition !== this.state.scrollPosition
        ) {
            return true
        } else {
            return false
        }
    }

    componentWillReceiveProps(
        nextProps
    ) {
        if (
            nextProps.data !== this.props.data
        ) {
            this.setState(
                initData(nextProps.data, nextProps.offsetKeyDate, nextProps.layout, nextProps.noAxisLabels)
            )
        }
    }

    getTransform(
        rad,
        width
    ) {
        let x = (0 - width / 2) * Math.cos(rad) - (0 - width / 2) * Math.sin(rad)
        let y = (0 - width / 2) * Math.sin(rad) + (0 - width / 2) * Math.cos(rad)

        return [
            { translateX: (-1 * x) - width / 2 },
            { translateY: (-1 * y) + width / 2 },
            { rotate: rad + 'rad' }
        ]
    }

    drawCoordinate(
        index,
        start,
        end,
        backgroundColor,
        lineStyle,
        isBlank,
        lastCoordinate,
        seriesIndex,
        noAxisLabels
    ) {
        let key = 'line' + index
        let dx = end.gap - start.gap
        let dy = end.ratioY - start.ratioY
        let size = Math.sqrt(dx * dx + dy * dy)
        let angleRad = -1 * Math.atan2(dy, dx)
        let height
        let top
        let topMargin = 20

        if (start.ratioY > end.ratioY) {
            height = start.ratioY
            top = -1 * size
        } else {
            height = end.ratioY
            top = -1 * (size - Math.abs(dy))
        }

        return (
            <View key={key}
                style={{
                height: this.props.layout.height + topMargin,
                justifyContent: 'flex-end'
            }} >

                <View style={[
                        {
                            width: dx,
                            height: height ? height : 1,
                            marginTop: topMargin
                        },
                        styles.coordinateWrapper
                    ]} >

                    <View style={[
                            {
                                top: top,
                                width: size,
                                height: size,
                                borderColor: isBlank
                                    ? backgroundColor
                                    : this.props.primaryColor,
                                borderTopWidth: 1,
                                transform: this.getTransform(angleRad, size)
                            },
                            styles.lineBox,
                            lineStyle
                        ]} />
                </View>

                {
                    !noAxisLabels && !lastCoordinate && seriesIndex === 0
                        ? (
                            <View style={[
                                styles.guideLine,
                                {
                                    width: dx
                                }
                                ]} />
                            )
                        : null
                }

                {
                    seriesIndex === this.state.sortedData.length - 1 &&
                    (
                        <TouchableWithoutFeedback
                            onPress={() => {
                                let selectedIndex = lastCoordinate
                                    ? index - 1
                                    : index
                                this.setState(
                                    { selectedIndex: selectedIndex },
                                    () => {
                                        if (typeof this.props.onPointClick === 'function') {
                                            this.props.onPointClick(
                                                this.state.sortedData[seriesIndex].data[selectedIndex]
                                            )
                                        }
                                    }
                                )
                            }} >

                            <View style={{
                                    width: dx,
                                    height: '100%',
                                    position: 'absolute',
                                    marginLeft: -1 * dx / 2
                                }} />
                        </TouchableWithoutFeedback>
                    )
                }
            </View>
        )
    }

    drawPoint(
        index,
        point,
        seriesColor,
        noAxisLabels
    ) {
        let key = 'point' + index
        let size = noAxisLabels ? 6 : 8
        let color = !seriesColor
            ? this.props.primaryColor
            : seriesColor

        if (this.state.selectedIndex === index) {
            color = this.props.selectedColor
        }

        return (
            <TouchableWithoutFeedback
                key={key}
                onPress={() => {
                    this.setState(
                        { selectedIndex: index },
                        () => {
                            if (typeof this.props.onPointClick === 'function') {
                                this.props.onPointClick(point)
                            }
                        }
                    )
                }}>

                <View style={[
                        styles.pointWrapper,
                        {
                            width: size,
                            height: size,

                            left: point.gap - size / 2,
                            bottom: point.ratioY - size / 2,

                            borderColor: color,
                            backgroundColor: color
                        }
                    ]} />
            </TouchableWithoutFeedback>
        )
    }

    drawCoordinates(
        data,
        seriesColor,
        seriesIndex,
        noAxisLabels
    ) {
        let result = []

        let lineStyle = {
            borderColor: !seriesColor
                ? this.props.primaryColor
                : seriesColor
        }

        let dataLength = data.length

        for (let i = 0; i < dataLength - 1; i++) {
            result.push(
                this.drawCoordinate(
                    i,
                    data[i],
                    data[i + 1],
                    'transparent',
                    lineStyle,
                    false,
                    false,
                    seriesIndex,
                    noAxisLabels
                )
            )
        }

        let lastData = Object.assign({}, data[dataLength - 1])
        let lastCoordinate = Object.assign({}, data[dataLength - 1])

        lastCoordinate.gap = lastCoordinate.gap + this.props.gap

        result.push(
            this.drawCoordinate(
                dataLength,
                lastData,
                lastCoordinate,
                'transparent',
                {},
                true,
                true,
                seriesIndex,
                noAxisLabels
            )
        )

        if (dataLength > 1) {
            result.push(
                this.drawPoint(
                    0,
                    data[0],
                    seriesColor,
                    noAxisLabels
                )
            )
        }

        for (let i = 0; i < dataLength - 1; i++) {
            result.push(
                this.drawPoint(
                    (i + 1),
                    data[i + 1],
                    seriesColor,
                    noAxisLabels
                )
            )
        }

        return result
    }

    getDistance(
        p1,
        p2
    ) {
        return Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2))
    }

    drawSelected(
        index,
        noSelectBox
    ) {
        if (this.state.sortedData.length === 0) return null

        let data = this.state.sortedData[0].data
        let dataObject = data[index]

        if (
            typeof(this.state.selectedIndex) === 'number' &&
            this.state.selectedIndex >= 0
        ) {
            if (!dataObject) return null

            // let reverse = true
            // let bottom = dataObject.ratioY

            let left = dataObject.gap
            let gap = 0

            if (index === data.length - 1) {
                left = data[index - 1].gap
                gap = dataObject.gap - left
            }

            // if (bottom > this.props.layout.height * 2 / 3) {
            //     reverse = false
            // }

            return (
                <View style={[
                    styles.selectedWrapper,
                    {
                        left: left,
                        justifyContent: 'center'
                    }
                    ]} >

                    <View style={[
                            styles.selectedLine,
                            {
                                backgroundColor: this.props.selectedColor,
                                marginLeft: gap
                            }
                        ]} />

                    {!noSelectBox && <View style={styles.selectedBox}>
                        {this.state.sortedData.map((series) => {
                            let dataObject = series.data[this.state.selectedIndex]

                            return (
                                <View key={series.seriesName}>
                                    {dataObject.x
                                            ? (<Text style={styles.tooltipTitle}>{dataObject.x}</Text>)
                                            : null}

                                    <View style={{
                                        flexDirection: 'row',
                                        paddingLeft: 5,
                                        alignItems: 'center'
                                        }} >

                                        <View style={{
                                            width: 10,
                                            height: 5,
                                            marginRight: 3,
                                            borderRadius: 2,
                                            backgroundColor: !series.seriesColor
                                                ? this.props.primaryColor
                                                : series.seriesColor
                                            }} />

                                        <Text style={styles.tooltipValue}>
                                            {numberWithCommas(dataObject.y, false)}
                                        </Text>
                                    </View>
                                </View>
                            )
                        })}
                    </View>}
                </View>
            )
        }

        return null
    }

    render() {
        let { style, layout, noAxisLabels, noSelectBox } = this.props
        let { sortedData, guideArray, selectedIndex, gap } = this.state

        return (
            sortedData.length > 0
                ? (
                    <View style={[styles.wrapper, style]}>
                        {!noAxisLabels && <View style={styles.yAxisLabelsWrapper}>
                            {drawYAxisLabels(guideArray, layout.height + 25)}
                        </View>}

                        <View style={{ padding: 5 }}>
                            <View ref='chartView' style={styles.chartViewWrapper}>
                                {!noAxisLabels && drawYAxis()}
                                {!noAxisLabels && drawGuideLine(guideArray)}
                                {
                                    sortedData.map((obj, index) => {
                                        return (
                                            <View key={'animated_' + index}
                                                style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'flex-end',
                                                    height: '100%',
                                                    position: index === 0
                                                        ? 'relative'
                                                        : 'absolute'
                                                }}>
                                                    {this.drawCoordinates(
                                                        obj.data,
                                                        obj.seriesColor,
                                                        index,
                                                        noAxisLabels
                                                    )}
                                            </View>
                                        )
                                    })
                                }
                                {this.drawSelected(selectedIndex, noSelectBox)}
                            </View>

                            {!noAxisLabels && drawXAxis()}
                            {!noAxisLabels && drawXAxisLabels(sortedData[0].data, gap)}

                        </View>
                    </View>
                )
            : null
        )
    }
}



// WEBPACK FOOTER //
// ./src/components/charts/index.js